'use client';

import { useEffect, useRef, useState } from 'react';

import type {
  HousingAvailabilityDay,
  HousingProperty,
} from '@eliadmin/core/src/api/housing/housing-types';
import { checkHousingAvailability } from '@eliadmin/core/src/api/housing/housing-api';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { Heading } from '@eliadmin/core/src/components/ui/heading';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { IoIosArrowDown } from 'react-icons/io';
import Link from 'next/link';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';

const MONTHS_SK = [
  'Január',
  'Február',
  'Marec',
  'Apríl',
  'Máj',
  'Jún',
  'Júl',
  'August',
  'September',
  'Október',
  'November',
  'December',
];

const YEARS = [2025, 2026, 2027, 2028];

const CELL_BASE =
  'flex items-center justify-center min-h-[37px] max-h-[37px] rounded-[24px] text-sm font-bold select-none';
const CELL_AVAILABLE = 'bg-[#CFFFCC] text-secondary';
const CELL_RESERVED = 'bg-[#FFD9CC] text-secondary';
const CELL_UNAVAILABLE = 'bg-[#F5F5F5] text-[#989898]';

interface AvailabilityCalendarProps {
  properties: HousingProperty[];
  locale: string;
}

export function AvailabilityCalendar({ properties, locale }: AvailabilityCalendarProps) {
  const { t } = useTranslations();
  const today = new Date();
  const [activePropertyId, setActivePropertyId] = useState(properties[0]?.id ?? 0);
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [days, setDays] = useState<HousingAvailabilityDay[]>([]);
  const [loading, setLoading] = useState(true);
  const [yearOpen, setYearOpen] = useState(false);
  const [monthOpen, setMonthOpen] = useState(false);

  const yearRef = useRef<HTMLDivElement>(null);
  const monthRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleOutsideClick(e: MouseEvent) {
      if (yearRef.current && !yearRef.current.contains(e.target as Node)) setYearOpen(false);
      if (monthRef.current && !monthRef.current.contains(e.target as Node)) setMonthOpen(false);
    }
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, []);

  useEffect(() => {
    if (!activePropertyId) return;
    const pad = (n: number) => String(n).padStart(2, '0');
    const dateFrom = `${year}-${pad(month)}-01`;
    const dateTo = `${year}-${pad(month)}-${pad(new Date(year, month, 0).getDate())}`;

    setLoading(true);
    checkHousingAvailability({
      locale,
      body: { property: { id: activePropertyId }, date_from: dateFrom, date_to: dateTo },
    })
      .then(res => setDays(res.data.property.days))
      .catch(() => setDays([]))
      .finally(() => setLoading(false));
  }, [activePropertyId, year, month, locale]);

  const pad = (n: number) => String(n).padStart(2, '0');
  const daysInMonth = new Date(year, month, 0).getDate();
  const dayMap = new Map(days.map(d => [d.date, d]));

  const cells: (HousingAvailabilityDay | null)[] = Array.from({ length: 35 }, (_, i) => {
    const dayNum = i + 1;
    if (dayNum > daysInMonth) return null;
    const dateStr = `${year}-${pad(month)}-${pad(dayNum)}`;
    return (
      dayMap.get(dateStr) ?? {
        id: 0,
        date: dateStr,
        is_available: false,
        is_blocked: false,
        is_reserved: false,
        reservation: null,
      }
    );
  });

  function getCellClass(day: HousingAvailabilityDay | null) {
    if (!day) return cn(CELL_BASE, 'opacity-0 pointer-events-none');
    if (day.is_reserved || day.is_blocked) return cn(CELL_BASE, CELL_RESERVED);
    if (day.is_available) return cn(CELL_BASE, CELL_AVAILABLE);
    return cn(CELL_BASE, CELL_UNAVAILABLE);
  }

  function formatDay(day: HousingAvailabilityDay | null) {
    if (!day) return '';
    const parts = day.date.split('-');
    return `${parseInt(parts[2])}.${parseInt(parts[1])}.`;
  }

  return (
    <section className={cn('custom-section', 'section-padding-lg-center')}>
      <div
        className={cn(
          'custom-container',
          'flex flex-col items-start justify-start gap-6 rounded-[24px] bg-[#F2EEE7] p-4 sm:p-6 md:flex-row md:justify-between lg:gap-8 lg:p-8 xl:p-12',
        )}
      >
        <div className="flex w-full max-w-[384px] flex-col items-start justify-start gap-6">
          <Heading title="Kalendár<br/>obsadenosti" />
          <Button asChild className="hidden md:flex">
            <Link href={'/rezervacia'}>{t('text1335')}</Link>
          </Button>
        </div>

        <div className="flex w-full flex-col items-start justify-start gap-2 md:max-w-[642px]">
          <div className="hide-scroll-bar flex w-full flex-col items-start justify-start gap-3 rounded-[24px] bg-white p-3 sm:flex-row sm:items-center sm:justify-between md:rounded-full">
            <div className="flex w-full items-center gap-1 rounded-full bg-[#F2EEE7] p-1 sm:w-fit">
              {properties.map(property => (
                <button
                  key={property.id}
                  type="button"
                  onClick={() => setActivePropertyId(property.id)}
                  className={cn(
                    'flex h-[42px] w-full items-center justify-center whitespace-nowrap rounded-full px-4 text-sm font-bold tracking-tight transition-colors sm:h-[45px] sm:w-fit sm:px-6 sm:text-base',
                    property.id === activePropertyId
                      ? 'bg-secondary text-white'
                      : 'text-secondary hover:bg-secondary/10',
                  )}
                >
                  {property.name}
                </button>
              ))}
            </div>

            <div className="flex w-full items-center gap-6 pl-3 pr-3 sm:w-fit md:pl-0 md:pr-5">
              <div ref={yearRef} className="relative w-full sm:w-fit">
                <button
                  type="button"
                  onClick={() => {
                    setYearOpen(o => !o);
                    setMonthOpen(false);
                  }}
                  className="flex w-full flex-col items-start gap-[2px] sm:w-fit"
                >
                  <p className="text-sm font-normal text-secondary">Rok</p>
                  <div className="flex items-center gap-[6px]">
                    <p className="text-base font-bold text-secondary">{year}</p>
                    <IoIosArrowDown
                      size={14}
                      className={cn(
                        'text-primary transition-transform duration-200',
                        yearOpen && 'rotate-180',
                      )}
                    />
                  </div>
                </button>
                {yearOpen && (
                  <div className="absolute left-0 top-full z-50 mt-2 overflow-hidden rounded-[16px] border border-border bg-white shadow-lg">
                    {YEARS.map(y => (
                      <button
                        key={y}
                        type="button"
                        onClick={() => {
                          setYear(y);
                          setYearOpen(false);
                        }}
                        className={cn(
                          'flex w-full items-center justify-center px-6 py-2.5 text-base font-bold text-secondary transition-colors hover:bg-[#F2EEE7]',
                          y === year && 'bg-[#F2EEE7]',
                        )}
                      >
                        {y}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div ref={monthRef} className="relative w-full sm:w-fit">
                <button
                  type="button"
                  onClick={() => {
                    setMonthOpen(o => !o);
                    setYearOpen(false);
                  }}
                  className="flex w-full flex-col items-start gap-[2px] sm:w-fit"
                >
                  <p className="text-sm font-normal text-secondary">Mesiac</p>
                  <div className="flex items-center gap-[6px]">
                    <p className="text-base font-bold text-secondary">{MONTHS_SK[month - 1]}</p>
                    <IoIosArrowDown
                      size={14}
                      className={cn(
                        'text-primary transition-transform duration-200',
                        monthOpen && 'rotate-180',
                      )}
                    />
                  </div>
                </button>
                {monthOpen && (
                  <div className="absolute right-0 top-full z-50 mt-2 overflow-hidden rounded-[16px] border border-border bg-white shadow-lg">
                    <div className="max-h-[264px] overflow-y-auto">
                      {MONTHS_SK.map((m, i) => (
                        <button
                          key={m}
                          type="button"
                          onClick={() => {
                            setMonth(i + 1);
                            setMonthOpen(false);
                          }}
                          className={cn(
                            'flex w-full items-center justify-center px-6 py-2.5 text-base font-bold text-secondary transition-colors hover:bg-[#F2EEE7]',
                            i + 1 === month && 'bg-[#F2EEE7]',
                          )}
                        >
                          {m}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div
            className={cn(
              'hide-scroll-bar relative w-full rounded-[24px] bg-white p-3 transition-opacity duration-200 sm:p-4 lg:p-6',
              loading && 'opacity-40',
            )}
          >
            <div className="hide-scroll-bar overflow-x-auto">
              <div
                className="grid gap-1 sm:gap-2"
                style={{ gridTemplateColumns: 'repeat(7, minmax(64px, 1fr))' }}
              >
                {cells.map((dayData, i) => (
                  <div key={i} className={getCellClass(dayData)}>
                    {formatDay(dayData)}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <Button className="flex w-full md:hidden">{t('text1335')}</Button>
      </div>
    </section>
  );
}
